import React, { useState } from "react";
import {
  Modal,
  Text,
  TextInput,
  ThemeIcon,
  Flex,
  Stack,
  TagsInput,
  Button,
  Tooltip,
  CopyButton,
  Textarea,
  Divider,
} from "@mantine/core";
import {
  IconCopy,
  IconCheck,
  IconShare,
  IconSend,
  IconMail,
  IconWorld,
  IconAt,
} from "@tabler/icons-react";
import { getInviteUrl } from "../Modals/InviteClientsModal/utils";
import { sendClientInvitations } from "./api";
import "./ClientModal.scss";
import { showNotification } from "@mantine/notifications";

interface ClientModalProps {
  opened: boolean;
  onClose: () => void;
}

const ClientModal: React.FC<ClientModalProps> = ({ opened, onClose }) => {
  const [emailPills, setEmailPills] = useState<string[]>([]);
  const [customMessage, setCustomMessage] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (emails: string[]) => {
    setError(null);
    const invalidEmails = emails.filter((email) => !validateEmail(email));

    if (invalidEmails.length > 0) {
      setError(
        `Invalid email${invalidEmails.length > 1 ? "s" : ""}: ${invalidEmails.join(", ")}`
      );
      return;
    }

    setEmailPills(emails);
  };

  const handleSubmit = async () => {
    if (emailPills.length === 0) {
      setError("Please enter at least one email address");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await sendClientInvitations(emailPills, customMessage);

      if (response.status === 200) {
        // Clear form and close modal on success
        setEmailPills([]);
        setCustomMessage("");
        showNotification({
          title: "Invitations sent",
          message: "Invitations sent successfully",
          color: "green",
        });
        onClose();
      } else {
        throw new Error(response.data.message ?? "Failed to send invitations");
      }
    } catch (err) {
      console.error("Error sending invitations:", err);
      setError("Failed to send invitations. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      className="client-modal"
      opened={opened}
      centered
      size={"lg"}
      w={1000}
      radius="sm"
      padding={32}
      onClose={onClose}
      title={
        <Flex align="center" justify="center" gap={8}>
          <ThemeIcon size="lg" radius="xl" color="#0077cc" variant="white">
            <IconShare size={24} />
          </ThemeIcon>{" "}
          <Text size="lg" fw={500} mb={0} className="invite-title">
            Invite your clients
          </Text>
        </Flex>
      }
      data-cy="inviteClientModal"
    >
      <Text size="sm" c="gray" mb={20} p={8} pt={0}>
        Easily invite your clients by sending them an email or simply copying
        the link to your client portal.
      </Text>
      <Stack gap={32}>
        <Stack>
          <TagsInput
            w="100%"
            radius="md"
            maxTags={5}
            label={
              <Flex align="center" gap={4}>
                <IconAt size={16} color="gray" />
                <Text className="share" my={0}>
                  Enter email(s) to invite
                </Text>
              </Flex>
            }
            withScrollArea={false}
            placeholder="Type or paste email addresses, separated by commas"
            value={emailPills}
            onChange={handleEmailChange}
            splitChars={[",", " "]}
            data={[]}
            clearable
            error={error}
          />

          <Textarea
            label={
              <Flex align="center" gap={4}>
                <IconMail size={16} color="gray" />
                <Text className="share" my={0}>
                  Add your custom message
                </Text>
              </Flex>
            }
            placeholder="Add a message"
            description="This message will be added to your email"
            maxRows={6}
            autosize
            radius="md"
            value={customMessage}
            onChange={(event) => setCustomMessage(event.currentTarget.value)}
          />
          <Button
            radius="md"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting || emailPills.length === 0}
          >
            <Flex align="flex-end" gap={8}>
              <IconSend size={18} />
              Send Invitations
            </Flex>
          </Button>
        </Stack>

        <Stack gap={4}>
          <Divider pb={16} mt={12} />
          <Flex align="center" gap={4}>
            <IconWorld size={16} color="gray" />
            <Text className="share" my={0}>
              Share your personalized public link with your clients.
            </Text>
          </Flex>
          <span className="invite-link-copy">
            <TextInput
              value={getInviteUrl()}
              readOnly
              style={{ width: "100%" }}
              data-cy="inviteClient"
              size="xs"
              radius="md"
              disabled
            />
            <CopyButton value={getInviteUrl()}>
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? "Copied" : "Copy"}
                  withArrow
                  position="top"
                >
                  <Button
                    aria-label="Copy invite link"
                    className="copy"
                    color={copied ? "teal" : "blue"}
                    onClick={copy}
                    radius="md"
                  >
                    {copied ? <IconCheck /> : <IconCopy />}
                  </Button>
                </Tooltip>
              )}
            </CopyButton>
          </span>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default ClientModal;
